import React from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";
import { Flame, ScrollText } from "lucide-react";

export function TermsPage() {
  return (
    <LegalWrapper title="Terms of Service" updated="July 2026">
      <p>Welcome to Furnace Design Lab (“Furnace,” “we,” “us”). By creating an account or paying for a plan you agree to these Terms.</p>
      <h2>1. Your account</h2>
      <p>You need to be at least 18. Keep your login safe — you’re responsible for anything done under it. One person or business per account. Team access is available on request.</p>
      <h2>2. What we provide</h2>
      <p>A browser-based prepress tool: halftone separations, AI vector cleanup, watermark removal, photo retouch, and related utilities. Free tier is capped at 3 exports/month. Paid tiers unlock unlimited use of listed features. Feature availability may change with reasonable notice.</p>
      <h2>3. Payments</h2>
      <p>Payments are processed by Stripe. Monthly plans (Pro/Creator) renew automatically until canceled. Lifetime plans are a single non-recurring charge. You can cancel a monthly plan anytime from your Stripe email receipt.</p>
      <h2>4. Refunds</h2>
      <p>15-day money-back guarantee on any first-time paid purchase. Email <a className="text-[#4aa4ff]" href="mailto:hello@furnacedesign.lab">hello@furnacedesign.lab</a> — no forms, no questions. See our <Link className="text-[#4aa4ff]" to="/refund">Refund Policy</Link> for details.</p>
      <h2>5. Your content</h2>
      <p>Artwork you upload stays yours. Halftone processing happens on your device — your art never leaves your browser. AI Vector Creator / Watermark Remover / Photo Upgrader send images to our AI provider only to run that request, and are never stored or used for training.</p>
      <h2>6. Acceptable use</h2>
      <p>Don’t use Furnace to remove watermarks from images you don’t own or have permission to modify, to infringe copyrights, or to create unlawful, deceptive, or hateful content. We may suspend accounts that violate these rules.</p>
      <h2>7. Warranty disclaimer</h2>
      <p>Furnace is provided “as is.” We work hard on quality but can’t guarantee it fits every workflow. To the maximum extent allowed by law, we exclude implied warranties.</p>
      <h2>8. Liability</h2>
      <p>Our total liability for any claim is capped at the amount you paid us in the 12 months before the claim.</p>
      <h2>9. Changes</h2>
      <p>We may update these Terms. Material changes will be announced by email or in-app at least 14 days in advance.</p>
      <h2>10. Contact</h2>
      <p><a className="text-[#4aa4ff]" href="mailto:hello@furnacedesign.lab">hello@furnacedesign.lab</a></p>
    </LegalWrapper>
  );
}

export function PrivacyPage() {
  return (
    <LegalWrapper title="Privacy Policy" updated="July 2026">
      <p>Short version: we store the minimum we need to run your account and process payments. We never sell your data.</p>
      <h2>What we collect</h2>
      <ul>
        <li><strong>Account:</strong> your email, name, hashed password (or Google profile if you sign in with Google).</li>
        <li><strong>Usage:</strong> plan status, export counts, feature usage for billing and rate limiting.</li>
        <li><strong>Payments:</strong> billing metadata via Stripe. We never see your full card number.</li>
        <li><strong>Support:</strong> the emails and messages you send us.</li>
      </ul>
      <h2>What we don’t store</h2>
      <ul>
        <li>Your artwork — Halftone Studio runs in your browser. Nothing leaves your device.</li>
        <li>AI tool inputs — sent to the AI provider only for that request, then discarded.</li>
      </ul>
      <h2>How we use it</h2>
      <p>To run your account, provide the tools you paid for, prevent abuse, communicate about your account, and send product updates you can opt out of. We do not sell your data. We do not use your artwork or prompts to train models.</p>
      <h2>Cookies</h2>
      <p>We use a single first-party cookie / localStorage entry to keep you signed in, plus Stripe’s essential cookies during checkout. That’s it — no ad-tech pixels.</p>
      <h2>Your rights</h2>
      <p>Email us for a copy of your data, to correct it, or to delete your account entirely. We’ll respond within 30 days.</p>
      <h2>Contact</h2>
      <p><a className="text-[#4aa4ff]" href="mailto:privacy@furnacedesign.lab">privacy@furnacedesign.lab</a></p>
    </LegalWrapper>
  );
}

export function RefundPage() {
  return (
    <LegalWrapper title="Refund Policy" updated="July 2026">
      <p>Furnace Design Lab is backed by a <strong>15-day money-back guarantee</strong> on any first-time paid purchase.</p>
      <h2>How to request a refund</h2>
      <p>Email <a className="text-[#4aa4ff]" href="mailto:hello@furnacedesign.lab">hello@furnacedesign.lab</a> from the account’s email address within 15 days of your purchase. No forms, no reasons required.</p>
      <h2>Turnaround</h2>
      <p>Refunds are issued within 3 business days. Depending on your bank, funds can take up to 10 days to appear.</p>
      <h2>Not eligible</h2>
      <ul>
        <li>Purchases older than 15 days.</li>
        <li>Repeated purchase-and-refund cycling.</li>
        <li>Fraudulent chargebacks.</li>
      </ul>
      <h2>Cancellation vs refund</h2>
      <p>Monthly subscriptions can be canceled anytime (no future charges) from your Stripe email receipt. Canceling stops renewals; a refund also returns money for the current period.</p>
    </LegalWrapper>
  );
}

function LegalWrapper({ title, updated, children }) {
  return (
    <div className="grain">
      <Navbar />
      <section className="relative py-16">
        <div className="forge-glow absolute inset-0" />
        <div className="relative mx-auto max-w-3xl px-4 sm:px-6">
          <span className="chip"><ScrollText className="h-3 w-3 text-[#4aa4ff]" /> Legal</span>
          <h1 className="display-font mt-3 text-4xl font-bold">{title}</h1>
          <p className="mt-1 text-xs text-muted-foreground">Last updated: {updated}</p>
          <div className="prose prose-invert mt-8 max-w-none text-muted-foreground">
            <div className="space-y-4 [&_h2]:mt-8 [&_h2]:display-font [&_h2]:text-xl [&_h2]:font-bold [&_h2]:text-foreground [&_p]:leading-relaxed [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-1 [&_a]:hover:underline">
              {children}
            </div>
          </div>
          <div className="mt-10 flex items-center gap-3 rounded-xl border border-border bg-card p-4 text-xs text-muted-foreground">
            <Flame className="h-4 w-4 text-[#4aa4ff]" />
            Questions? Email <a className="text-[#4aa4ff]" href="mailto:hello@furnacedesign.lab">hello@furnacedesign.lab</a>.
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
